import { useEffect } from "react";
import { useDebounce } from "./use-debounce";
import { hasContent } from "views/inbox/containers/createReport/helpers/htmlHelpers";
import { isContentSizeValid } from "helpers/contentSizeValidator";
import { FormValidation } from "app/store/InboxReplyContext";

interface UseDebouncedContentValidationParams {
  text: string;
  validation: FormValidation;
  recipients: any[];
  showSubjectLine?: boolean;
  updateFormState: (state: any) => void;
  onValidationChange?: (isValid: boolean) => void;
}

/**
 * Hook that debounces content validation to improve performance
 * Validates content size and body content after user stops typing
 */
export function useDebouncedContentValidation({
  text,
  validation,
  recipients,
  showSubjectLine,
  updateFormState,
  onValidationChange
}: UseDebouncedContentValidationParams) {
  const debouncedText = useDebounce(text, 300);

  useEffect(() => {
    const newBodyValidation = hasContent(debouncedText) ? "VALID" : "INVALID";
    const newContentSizeValidation = isContentSizeValid(debouncedText)
      ? "VALID"
      : "INVALID";

    updateFormState({
      validation: {
        ...validation,
        body: newBodyValidation,
        contentSize: newContentSizeValidation
      }
    });

    if (onValidationChange) {
      const valid =
        recipients.length > 0 &&
        (!showSubjectLine || validation.title === "VALID") &&
        newBodyValidation === "VALID" &&
        newContentSizeValidation === "VALID";
      onValidationChange(valid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedText]);
}
